import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { useAlert } from '../../context/AlertContext';
import { ASSESSMENT_TYPE_DESCRIPTIONS } from '../../utils/hiraConstants';

const HIRACreate = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showError } = useAlert();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assessment_type: 'Activity-based',
    department: '',
    location: '',
    review_date: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/risk-assessments`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Navigate to detail page to add hazards
      navigate(`/hira/${response.data.id}`);
    } catch (err) {
      console.error('Failed to create assessment:', err);
      showError('Failed to create risk assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/hira')}
            className="text-[#1B5C94] hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Risk Assessments
          </button>
          <h1 className="text-3xl font-bold text-gray-800">Create New Risk Assessment</h1>
          <p className="text-gray-600 mt-1">Fill in the basic details. You'll add hazards in the next step.</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Assessment Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="e.g., Welding Operations - Main Workshop"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder="Provide additional details about this risk assessment..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
            />
          </div>

          {/* Assessment Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Assessment Type <span className="text-red-500">*</span>
            </label>
            <select
              name="assessment_type"
              value={formData.assessment_type}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
            >
              <option value="Activity-based">Activity-based</option>
              <option value="Task-based">Task-based</option>
              <option value="JSA">Job Safety Analysis (JSA)</option>
              <option value="General">General Assessment</option>
            </select>
            {/* Description text */}
            {formData.assessment_type && (
              <p className="text-xs text-gray-600 mt-2">
                {ASSESSMENT_TYPE_DESCRIPTIONS[formData.assessment_type]}
              </p>
            )}
          </div>

          {/* Department & Location */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Department
              </label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleChange}
                placeholder="e.g., Operations, Maintenance"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="e.g., Workshop A, Site 2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
              />
            </div>
          </div>

          {/* Review Date */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Next Review Date
            </label>
            <input
              type="date"
              name="review_date"
              value={formData.review_date}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Recommended: Review annually or when conditions change</p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border-l-4 border-[#1B5C94] p-4 rounded">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-[#1B5C94] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-gray-700">
                <p className="font-semibold mb-1">Next Steps:</p>
                <p>After creating this assessment, you'll be able to add hazards, assess risks using the 5×5 matrix, 
                and implement control measures following the hierarchy of controls.</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/hira')}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.title}
              className="flex-1 px-6 py-3 bg-[#1B5C94] text-white font-semibold rounded-lg hover:bg-[#164A7B] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Assessment'}
            </button>
          </div>
        </form>
      </div>
    </div>
      </div>
  );
};

export default HIRACreate;
